import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import "./style.css";
import CircularProgress from "@mui/material/CircularProgress";
import { onboardingStatusAPI } from "../../../store/features/checkOnboardingStatus";
import OverViewChart from "../../../components/dashboardcomponents/OverViewChart";
import BarChart from "../../../components/dashboardcomponents/Home/BarChart";
import FirstResponseBarChart from "../../../components/dashboardcomponents/Analytics/Chats/StackedBarChart";
import { ReactComponent as BlueArrow } from "../../../assets/images/ChatImages/bluearrow.svg";
import { ReactComponent as RedArrow } from "../../../assets/images/ChatImages/redarrow.svg";
import DonutChart from "../../../components/dashboardcomponents/Analytics/Overview/DonutChat";
import TagUsage from "../../../components/dashboardcomponents/Analytics/Chats/TagUsage";
import MainHeaderAnalytics from "../../../components/dashboardcomponents/Analytics/MainHeaderAnalytics";
import { getChatAnalyticsStats } from "../../../store/features/getChatAnalyticsStats";
import { getCsatAnalyticsStats } from "../../../store/features/getCsatAnalyticsStats";
import { getChatPerformanceStats } from "../../../store/features/getChatPerformanceStats";
import { getChatTagUsageStats } from "../../../store/features/getChatTagUsageStats";
import { getAgentResponseTimeStats } from "../../../store/features/getAgentResponseTimeStats";
import dayjs from "dayjs";
import { useAnalyticsExport } from "../../../hooks/useAnalyticsExport";
import { useAnalyticsGroupId } from "../../../hooks/useAnalyticsGroupId";

function MainChatsAnalytics() {
  const [loading, isLoading] = useState(true);
  const [currentRange, setCurrentRange] = useState("7d");
  const [activeTab, setActiveTab] = useState("Chats handled vs missed");
  const [activeTimeFilter, setActiveTimeFilter] = useState("7 days");
  const [dateRange, setDateRange] = useState({
    startDate: dayjs().subtract(6, "day").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentLanguage = useSelector(
    (state) => state.language.currentLanguage
  );
  const userData = useSelector((state) => state.loginToken);
  const chatAnalyticsStats = useSelector((state) => state.chatAnalyticsStats);
  const csatAnalyticsStats = useSelector((state) => state.csatAnalyticsStats);
  const chatPerformanceStats = useSelector(
    (state) => state.chatPerformanceStats
  );
  const chatTagUsageStats = useSelector((state) => state.chatTagUsageStats);
  const agentResponseTimeStats = useSelector(
    (state) => state.agentResponseTimeStats
  );

  const { selectedGroupId, setSelectedGroupId } = useAnalyticsGroupId();

  const {
    exportSectionOptions,
    exportSection,
    setExportSection,
    handleExport,
    exportLoading,
  } = useAnalyticsExport("chat", currentRange, dateRange, selectedGroupId);

  useEffect(() => {
    const onboardingStatusDispatcher = dispatch(
      onboardingStatusAPI({ requestHeaders: userData?.response?.user?._id })
    );
    onboardingStatusDispatcher.then(async (response) => {
      if (response?.payload?.onboarded === false) {
        navigate("/on-boarding", { replace: true });
      } else {
        isLoading(false);
      }
    });
  }, []);

  // Fetch chat analytics data when component mounts or date range / group changes
  useEffect(() => {
    if (!selectedGroupId) return;
    if (!userData?.response?.user?.business_id) return;

    const fetchData = () => {
      if (currentRange === "custom") {
        dispatch(
          getChatAnalyticsStats({
            range: currentRange,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            group_id: selectedGroupId,
          })
        );
        dispatch(
          getCsatAnalyticsStats({
            range: currentRange,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            group_id: selectedGroupId,
          })
        );
        dispatch(
          getChatPerformanceStats({
            range: currentRange,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            group_id: selectedGroupId,
          })
        );
        dispatch(
          getChatTagUsageStats({
            range: currentRange,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            group_id: selectedGroupId,
          })
        );
        dispatch(
          getAgentResponseTimeStats({
            range: currentRange,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            group_id: selectedGroupId,
          })
        );
      } else {
        dispatch(
          getChatAnalyticsStats({
            range: currentRange,
            group_id: selectedGroupId,
          })
        );
        dispatch(
          getCsatAnalyticsStats({
            range: currentRange,
            group_id: selectedGroupId,
          })
        );
        dispatch(
          getChatPerformanceStats({
            range: currentRange,
            group_id: selectedGroupId,
          })
        );
        dispatch(
          getChatTagUsageStats({
            range: currentRange,
            group_id: selectedGroupId,
          })
        );
        dispatch(
          getAgentResponseTimeStats({
            range: currentRange,
            group_id: selectedGroupId,
          })
        );
      }
    };

    fetchData();
  }, [
    dispatch,
    currentRange,
    dateRange.startDate,
    dateRange.endDate,
    userData?.response?.user?.business_id,
    selectedGroupId,
  ]);

  const t = useSelector(
    (state) => state.language.translations[currentLanguage]
  );

  // Helper function to format time in seconds to readable format
  const formatTime = (seconds) => {
    // Handle null, undefined, or 0 values
    if (!seconds || seconds === 0) {
      return "0s";
    }

    // Convert to number if it's a string
    const numSeconds = Number(seconds);

    if (numSeconds < 60) {
      // For values less than 60 seconds, show with 1 decimal place if less than 1 second
      if (numSeconds < 1) {
        return `${numSeconds.toFixed(1)}s`;
      }
      return `${Math.round(numSeconds)}s`;
    } else if (numSeconds < 3600) {
      const minutes = Math.floor(numSeconds / 60);
      const remainingSeconds = Math.round(numSeconds % 60);
      if (remainingSeconds === 0) {
        return `${minutes}m`;
      }
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      const hours = Math.floor(numSeconds / 3600);
      const minutes = Math.floor((numSeconds % 3600) / 60);
      if (minutes === 0) {
        return `${hours}h`;
      }
      return `${hours}h ${minutes}m`;
    }
  };

  // Debug: Log the chat analytics stats
  console.log("Chat Analytics Stats:", chatAnalyticsStats);
  console.log(
    "avgResponseTime value:",
    chatAnalyticsStats?.metrics?.avgResponseTime?.value
  );
  console.log(
    "avgChatDuration value:",
    chatAnalyticsStats?.metrics?.avgChatDuration?.value
  );

  // Debug: Log the CSAT analytics stats
  console.log("CSAT Analytics Stats:", csatAnalyticsStats);
  console.log(
    "CSAT good percentage:",
    csatAnalyticsStats?.metrics?.good?.percentage
  );
  console.log(
    "CSAT bad percentage:",
    csatAnalyticsStats?.metrics?.bad?.percentage
  );

  // Debug: Log the chat performance stats
  console.log("Chat Performance Stats:", chatPerformanceStats);
  console.log("Chat Performance Data:", chatPerformanceStats?.chartData);

  // Debug: Log the chat tag usage stats
  console.log("Chat Tag Usage Stats:", chatTagUsageStats);
  console.log("Chat Tag Usage Data:", chatTagUsageStats?.tagData);

  // Debug: Log the agent response time stats
  console.log("Agent Response Time Stats:", agentResponseTimeStats);
  console.log("Agent Response Time Data:", agentResponseTimeStats?.chartData);

  // Test formatTime with sample values
  console.log(
    "formatTime(0.21466666666666664):",
    formatTime(0.21466666666666664)
  );
  console.log("formatTime(955.2933448275862):", formatTime(955.2933448275862));

  // Create data array using real API data
  // Only show percentage change when 7-day filter is selected
  const showPercentageChange = currentRange === "7d";
  const dataArray = [
    {
      title: "Total Chats",
      number: chatAnalyticsStats?.metrics?.totalChats?.value || 0,
      textPercentage: showPercentageChange
        ? chatAnalyticsStats?.metrics?.totalChats?.change || 0
        : null,
      value: showPercentageChange ? t.STATS_VLW : " ",
      logo: showPercentageChange ? (
        chatAnalyticsStats?.metrics?.totalChats?.change >= 0 ? (
          <BlueArrow />
        ) : (
          <RedArrow />
        )
      ) : null,
    },
    {
      title: "Queued Chats",
      number: chatAnalyticsStats?.metrics?.queuedChats?.value || 0,
      textPercentage: showPercentageChange
        ? chatAnalyticsStats?.metrics?.queuedChats?.change || 0
        : null,
      value: showPercentageChange ? t.STATS_VLW : " ",
      logo: showPercentageChange ? (
        chatAnalyticsStats?.metrics?.queuedChats?.change >= 0 ? (
          <BlueArrow />
        ) : (
          <RedArrow />
        )
      ) : null,
    },
    {
      title: "Missed Chats",
      number: chatAnalyticsStats?.metrics?.missedChats?.value || 0,
      textPercentage: showPercentageChange
        ? chatAnalyticsStats?.metrics?.missedChats?.change || 0
        : null,
      value: showPercentageChange ? t.STATS_VLW : " ",
      logo: showPercentageChange ? (
        chatAnalyticsStats?.metrics?.missedChats?.change >= 0 ? (
          <BlueArrow />
        ) : (
          <RedArrow />
        )
      ) : null,
    },
    {
      title: "Average Response Time",
      number: formatTime(
        chatAnalyticsStats?.metrics?.avgResponseTime?.value || 0
      ),
      textPercentage: showPercentageChange
        ? chatAnalyticsStats?.metrics?.avgResponseTime?.change || 0
        : null,
      value: showPercentageChange ? t.STATS_VLW : " ",
      logo: showPercentageChange ? (
        chatAnalyticsStats?.metrics?.avgResponseTime?.change >= 0 ? (
          <BlueArrow />
        ) : (
          <RedArrow />
        )
      ) : null,
      isTimeValue: true,
    },
    {
      title: "Average Chat Duration",
      number: formatTime(
        chatAnalyticsStats?.metrics?.avgChatDuration?.value || 0
      ),
      textPercentage: showPercentageChange
        ? chatAnalyticsStats?.metrics?.avgChatDuration?.change || 0
        : null,
      value: showPercentageChange ? t.STATS_VLW : " ",
      logo: showPercentageChange ? (
        chatAnalyticsStats?.metrics?.avgChatDuration?.change >= 0 ? (
          <BlueArrow />
        ) : (
          <RedArrow />
        )
      ) : null,
      isTimeValue: true,
    },
  ];

  // Transform chat performance data for BarChart
  const transformedChatData =
    chatPerformanceStats?.chartData?.map((item) => ({
      day: item.label,
      primary: item.handled || 0,
      secondary: item.missed || 0,
      date: item.date,
    })) || [];

  // Transform agent response time data for StackedBarChart
  const transformedAgentData =
    agentResponseTimeStats?.chartData?.map((periodData) => {
      const chartItem = {
        day: periodData.label,
        period: periodData.period,
        topAgents: periodData.topAgents || [], // Pass the original topAgents array
      };

      // Add top 5 agents data for backward compatibility
      periodData.topAgents?.forEach((agent, index) => {
        if (index < 5) {
          // Only take top 5 agents
          const agentKey = `agent${index + 1}`;
          const agentTimeKey = `agent${index + 1}Time`;
          const agentNameKey = `agent${index + 1}Name`;
          const agentEmailKey = `agent${index + 1}Email`;

          chartItem[agentKey] = agent.chatsHandled || 0;
          chartItem[agentTimeKey] = agent.avgResponseTimeFormatted || "0m 0s";
          chartItem[agentNameKey] = agent.name || "Unknown";
          chartItem[agentEmailKey] = agent.email || "";
        }
      });

      // Fill remaining agent slots with default values if less than 5 agents
      for (let i = periodData.topAgents?.length || 0; i < 5; i++) {
        const agentKey = `agent${i + 1}`;
        const agentTimeKey = `agent${i + 1}Time`;
        const agentNameKey = `agent${i + 1}Name`;
        const agentEmailKey = `agent${i + 1}Email`;

        chartItem[agentKey] = 0;
        chartItem[agentTimeKey] = "0m 0s";
        chartItem[agentNameKey] = "No Agent";
        chartItem[agentEmailKey] = "";
      }

      return chartItem;
    }) || [];

  const firstResponseData = transformedAgentData;

  // Transform chat tag usage data for TagUsage component
  const tagUsageData =
    chatTagUsageStats?.tagData?.map((item) => ({
      name: item.name,
      count: item.count,
      percentage: item.percentage,
      tag_id: item.tag_id,
    })) || [];

  // Create CSAT data using real API data (only Positive and Negative, no Neutral)
  const csatScoreData = [
    {
      count: csatAnalyticsStats?.metrics?.good?.count || 0,
      name: "Positive",
      value: csatAnalyticsStats?.metrics?.good?.percentage || 0,
      color: "#52C41A",
    },
    {
      name: "Negative",
      count: csatAnalyticsStats?.metrics?.bad?.count || 0,
      value: csatAnalyticsStats?.metrics?.bad?.percentage || 0,
      color: "#FF4D4F",
    },
  ];

  const handleDateChange = (newDateRange) => {
    setDateRange(newDateRange);

    const start = dayjs(newDateRange.startDate);
    const end = dayjs(newDateRange.endDate);
    const today = dayjs().startOf("day");

    if (start.isSame(today, "day") && end.isSame(today, "day")) {
      setCurrentRange("today");
      setActiveTimeFilter("today");
    } else if (
      start.isSame(today.subtract(1, "day"), "day") &&
      end.isSame(today.subtract(1, "day"), "day")
    ) {
      setCurrentRange("yesterday");
      setActiveTimeFilter("yesterday");
    } else if (
      start.isSame(today.subtract(6, "day"), "day") &&
      end.isSame(today, "day")
    ) {
      setCurrentRange("7d");
      setActiveTimeFilter("7 days");
    } else if (
      start.isSame(today.subtract(29, "day"), "day") &&
      end.isSame(today, "day")
    ) {
      setCurrentRange("30d");
      setActiveTimeFilter("30 days");
    } else if (
      start.isSame(today.subtract(12, "month").add(1, "day"), "day") &&
      end.isSame(today, "day")
    ) {
      setCurrentRange("1y");
      setActiveTimeFilter("12 months");
    } else {
      setCurrentRange("custom");
      setActiveTimeFilter("custom");
    }
  };

  return (
    <div className="Dashboard_Main_Containerxx">
      <MainHeaderAnalytics
        title="Chats"
        dateRange={dateRange}
        onDateChange={handleDateChange}
        exportSectionOptions={exportSectionOptions}
        exportSection={exportSection}
        onExportSectionChange={setExportSection}
        onExport={handleExport}
        exportLoading={exportLoading}
        selectedGroupId={selectedGroupId}
        onGroupChange={setSelectedGroupId}
      />

      <OverViewChart data={dataArray} loading={chatAnalyticsStats?.loading} />

      <p className="overview_main" style={{ marginTop: "32px" }}>
        {"Chats handled vs missed"}
      </p>
      <div style={{ marginBottom: "40px" }}>
        <BarChart
          data={transformedChatData}
          selectedTab={activeTab}
          activeTimeFilter={activeTimeFilter}
          loading={chatPerformanceStats?.loading}
        />
      </div>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          mb: 4,
        }}
      >
        <TagUsage
          title="Tag Usage"
          description="Tag usage across chats."
          bgcolor="#D6EDFF"
          data={tagUsageData}
          loading={chatTagUsageStats?.loading}
        />
        <DonutChart
          title="CSAT Score"
          description="Live chat customer satisfaction score"
          data={csatScoreData}
          loading={csatAnalyticsStats?.loading}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <FirstResponseBarChart
          title="First Response Time"
          description="Average reply speed"
          data={firstResponseData}
          loading={agentResponseTimeStats?.loading}
          currentRange={currentRange}
          dateRange={dateRange}
        />
      </Box>
    </div>
  );
}

export default MainChatsAnalytics;
